import React from 'react';
import moment from 'moment';
import { InstanceDetailsProps } from './types';
import { styles } from './styles';
import { CollapsiblePanel, DetailField } from '@v2/components';
import type { ClientManifestPlugin } from '@v2/types';

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return moment.utc(date).local().format('M/D/YYYY LT');
};

const getPluginStatus = (plugin: ClientManifestPlugin): { label: string; className: string } => {
  if (plugin.status?.isRunning) return { label: 'Running', className: styles.statusRunning };
  if (plugin.status?.isInstalled) return { label: 'Installed', className: styles.statusInstalled };
  if (plugin.status?.isCached) return { label: 'Cached', className: styles.statusCached };
  return { label: 'In Manifest', className: styles.statusDefault };
};

const InstanceDetails: React.FC<InstanceDetailsProps> = ({
  instance,
  manifest,
  manifestLoading,
}) => {
  const plugins = manifest?.plugins ?? [];

  return (
    <div className={styles.fieldContainer}>
      <DetailField label="Instance ID">{instance.id}</DetailField>
      <DetailField label="Practice Name">{instance.practice?.name ?? '-'}</DetailField>
      <DetailField label="PIMS">{instance.practice?.pimsInfo?.displayName ?? '-'}</DetailField>
      <DetailField label="PIMS Version">
        {instance.practice?.pimsVersionInfo?.version ?? '-'}
      </DetailField>
      <DetailField label="Hardware ID">{instance.hardwareId ?? '-'}</DetailField>
      <DetailField label="Last Run">{formatDate(instance.lastRun)}</DetailField>
      {manifest && (
        <>
          <DetailField label="MC Version">{manifest.missionControlVersion ?? '-'}</DetailField>
          <DetailField label="Schema Version">{manifest.schemaVersion ?? '-'}</DetailField>
        </>
      )}
      <div className={styles.fieldFullWidth}>
        <CollapsiblePanel title={`Manifest Plugins (${plugins.length})`} defaultExpanded={false}>
          {manifestLoading ? (
            <div className={styles.emptyText}>Loading manifest...</div>
          ) : plugins.length === 0 ? (
            <div className={styles.emptyText}>No plugins in manifest</div>
          ) : (
            <div className={styles.pluginGrid}>
              {plugins.map(plugin => {
                const status = getPluginStatus(plugin);
                return (
                  <div key={`${plugin.uid}-${plugin.name}`} className={styles.pluginCard}>
                    <span className={styles.pluginName}>{plugin.name}</span>
                    <span className={styles.pluginMeta}>v{plugin.version}</span>
                    <span className={`${styles.statusBadge} ${status.className}`}>
                      {status.label}
                    </span>
                    {plugin.isInherited && <span className={styles.pluginMeta}>Inherited</span>}
                  </div>
                );
              })}
            </div>
          )}
        </CollapsiblePanel>
      </div>
    </div>
  );
};

export default InstanceDetails;
